import moment from "moment-timezone";
import "moment/locale/es";
import { getEventStartMoment } from "../events/eventsHelpers";

moment.locale("es");

const STATUS_TO_VARIANT = {
  ACTIVE: "success",
  PROSPECTO: "warning",
  APPROVED: "warning",
  INACTIVO: "error",
  EXPIRED: "error",
  DESARROLLADOR: "info",
  TEMPORALLY_LEAVE: "info",
};

const STATUS_TO_LABEL = {
  ACTIVE: "Activo",
  PROSPECTO: "Prospecto",
  APPROVED: "Aprobado",
  INACTIVO: "Inactivo",
  EXPIRED: "Expirado",
  DESARROLLADOR: "Desarrollador",
  TEMPORALLY_LEAVE: "Baja Temporal",
};

export const resolveStatusVariant = (status) => STATUS_TO_VARIANT[status] || "neutral";
export const resolveStatusLabel = (status) => STATUS_TO_LABEL[status] || (status || "N/D");

export const resolveOrganizationId = (user, userMembership) => {
  if (!user) return null;
  if (userMembership && userMembership.organization) {
    return userMembership.organization._id || userMembership.organization.id;
  }
  if (user.headOffice) {
    if (typeof user.headOffice === "object") {
      return user.headOffice.id || user.headOffice._id;
    }
    return user.headOffice;
  }
  return null;
};

export const resolveFirstName = (user) => {
  if (!user) return "";
  if (user.name) return user.name.split(/\s+/)[0];
  if (user.firstLastname) return user.firstLastname.split(/\s+/)[0];
  if (user.company) return user.company;
  return "";
};

export const resolveMembershipType = (user) => {
  if (!user) return "N/D";
  if (user.memberOf && user.memberOf.affiliationType) return user.memberOf.affiliationType;
  if (user.memberInformation && user.memberInformation.type) return user.memberInformation.type;
  return "N/D";
};

export const resolveExpirationDate = (user) => {
  if (!user || !user.affiliation) return null;
  const localDetails = user.affiliation.local && user.affiliation.local.details;
  if (localDetails && localDetails.length > 0) {
    const info = localDetails[0].affiliateInformation;
    if (info && (info.expirationDate || info.inactiveDate)) {
      return info.expirationDate || info.inactiveDate;
    }
  }
  const nationalDetails = user.affiliation.national && user.affiliation.national.details;
  if (nationalDetails && nationalDetails.length > 0) {
    const info = nationalDetails[0].affiliateInformation;
    if (info && (info.expirationDate || info.inactiveDate)) {
      return info.expirationDate || info.inactiveDate;
    }
  }
  return null;
};

const resolveAffiliationSource = (user, ownerAffiliation) => {
  const ownAffiliation = user && user.affiliation;
  const ownDetails = ["national", "local"].some((level) => {
    const details = ownAffiliation && ownAffiliation[level] && ownAffiliation[level].details;
    return Array.isArray(details) && details.length > 0;
  });

  if (ownDetails) return ownAffiliation;
  if (ownerAffiliation && ownerAffiliation.affiliation) return ownerAffiliation.affiliation;
  return ownAffiliation || null;
};

const resolveAffiliationDetails = (user, ownerAffiliation) => {
  const source = resolveAffiliationSource(user, ownerAffiliation);
  if (!source) return [];

  return ["national", "local"].reduce((details, level) => {
    const levelDetails = source[level] && source[level].details;
    if (Array.isArray(levelDetails)) return details.concat(levelDetails);
    if (levelDetails && typeof levelDetails === "object") return details.concat(levelDetails);
    return details;
  }, []);
};

const resolveIndulgenceConfig = (detail) => {
  const affiliationType = detail && detail.affiliationType;
  return (
    (affiliationType && affiliationType.indulgenceConfig) ||
    (detail && detail.indulgenceConfig) ||
    null
  );
};

const resolveIndulgenceEndDate = (detail) => {
  const inactiveDate = detail && detail.affiliateInformation && detail.affiliateInformation.inactiveDate;
  if (!inactiveDate) return null;

  const baseDate = moment(inactiveDate);
  if (!baseDate.isValid()) return null;

  const indulgence = resolveIndulgenceConfig(detail);
  if (!indulgence || !indulgence.active) return baseDate;

  const endDate = baseDate.clone();
  const periodQuantity = Number(indulgence.periodQuantity) || 0;
  if (indulgence.period === "DAYS") endDate.add(periodQuantity, "days");
  if (indulgence.period === "MONTHS") endDate.add(periodQuantity, "months");
  return endDate;
};

export const resolveIndulgenceEndDateForUser = (user, ownerAffiliation) => {
  const dates = resolveAffiliationDetails(user, ownerAffiliation)
    .map(resolveIndulgenceEndDate)
    .filter(Boolean);
  if (!dates.length) return null;
  return dates.reduce((earliest, current) =>
    current.isBefore(earliest) ? current : earliest
  );
};

export const isInIndulgencePeriod = (user, ownerAffiliation, now = moment()) => {
  if (!user) return false;

  const details = resolveAffiliationDetails(user, ownerAffiliation)
    .map((detail) => {
      const inactiveDate = detail && detail.affiliateInformation && detail.affiliateInformation.inactiveDate;
      const endDate = resolveIndulgenceEndDate(detail);
      const indulgence = resolveIndulgenceConfig(detail);
      return {
        inactiveDate,
        endDate,
        hasActiveIndulgence: Boolean(indulgence && indulgence.active),
        hasIndulgenceConfig: Boolean(indulgence),
      };
    })
    .filter(({ inactiveDate }) => inactiveDate);

  if (!details.length) return false;

  return details.every(({ inactiveDate, endDate, hasActiveIndulgence, hasIndulgenceConfig }) => {
    const startDate = moment(inactiveDate);
    if (!startDate.isValid() || !now.isAfter(startDate)) return false;
    if (hasIndulgenceConfig && !hasActiveIndulgence) return false;
    // En algunos snapshots de usuario la configuración no viene poblada;
    // en ese caso la fecha de inactivación sigue siendo la señal de tolerancia.
    return !hasActiveIndulgence || now.isSameOrBefore(endDate);
  });
};

export const isExpired = (user) => {
  if (!user) return false;
  return user.validationStatus === "EXPIRED" || user.validationStatus === "INACTIVO";
};

export const isProspect = (user) => {
  if (!user) return false;
  return (
    user.validationStatus === "PROSPECTO" || user.validationStatus === "APPROVED"
  );
};

export const formatDate = (value, format = "DD MMM YYYY") => {
  if (!value) return "";
  const m = moment(value);
  return m.isValid() ? m.format(format) : "";
};

export const resolveUpcomingEvents = (purchasedEvents, events, limit = 3) => {
  const now = moment();

  if (Array.isArray(events) && events.length > 0) {
    return events
      .filter(
        (ev) =>
          ev &&
          getEventStartMoment(ev) &&
          getEventStartMoment(ev).isSameOrAfter(now)
      )
      .sort(
        (a, b) =>
          getEventStartMoment(a).valueOf() - getEventStartMoment(b).valueOf()
      )
      .slice(0, limit)
      .map((ev) => ({ event: ev }));
  }

  if (Array.isArray(purchasedEvents) && purchasedEvents.length > 0) {
    return purchasedEvents
      .filter(
        (it) =>
          it &&
          it.event &&
          getEventStartMoment(it)
      )
      .filter((it) =>
        getEventStartMoment(it).isSameOrAfter(now)
      )
      .sort(
        (a, b) =>
          getEventStartMoment(a).valueOf() - getEventStartMoment(b).valueOf()
      )
      .slice(0, limit);
  }

  return [];
};

const isTruthy = (v) => v !== null && v !== undefined && v !== "N/D" && v !== "N/A" && v !== "";

export const resolveRenewLink = (user, userMembership) => {
  if (!user) return "/member/renew/2";
  const paymentType = user.headOffice && user.headOffice.paymentType;
  let link = `/member/renew/2/${user._id || ""}`;
  const branch = userMembership && userMembership.organization
    ? userMembership.organization._id
    : user.branchId;
  if (isTruthy(branch) && paymentType !== "NATIONAL") {
    link = `${link}/${branch}`;
  }
  return link;
};

export const resolveRenewCtaLabel = (status) => {
  const payMembership = status && status !== "ACTIVE";
  if (!payMembership) return "Renovar ahora";
  return "Pagar membresía";
};

export const resolveRenewAlert = (status, ableToRenewNational) => {
  if (ableToRenewNational) return "Tu membresía está próxima a vencer";
  switch (status) {
    case "EXPIRED":
      return "Tu membresía ha expirado";
    case "INACTIVO":
      return "Tu membresía está inactiva";
    default:
      return "";
  }
};

export const computeAbleToRenewByAffiliate = (affiliateInfo) => {
  if (!affiliateInfo) return false;
  const currentDate = moment();
  const { status = null, affiliateInformation = null, affiliationType = null } = affiliateInfo;
  if (!affiliateInformation || status !== "ACTIVE") return false;

  const prepaidConfig = affiliationType && affiliationType.prepaidConfig;
  const dateToExpire = affiliateInformation.inactiveDate || affiliateInformation.expirationDate;
  if (!dateToExpire) return false;

  const memberExpirationDate = moment(dateToExpire);
  if (!memberExpirationDate.isValid()) return false;

  if (prepaidConfig && prepaidConfig.active) {
    const { periodQuantity = 0, period = "days" } = prepaidConfig;
    memberExpirationDate.add(-periodQuantity, String(period).toLowerCase());
  }

  return currentDate.isSameOrAfter(memberExpirationDate);
};

export const computeNextRenewDate = (affiliateInfo) => {
  if (!affiliateInfo) return null;
  const { status = null, affiliateInformation = null, affiliationType = null } = affiliateInfo;
  if (!affiliateInformation || status !== "ACTIVE") return null;

  const prepaidConfig = affiliationType && affiliationType.prepaidConfig;
  const dateToExpire = affiliateInformation.inactiveDate || affiliateInformation.expirationDate;
  if (!dateToExpire) return null;

  const renewableDate = moment(dateToExpire);
  if (!renewableDate.isValid()) return null;

  if (prepaidConfig && prepaidConfig.active) {
    const { periodQuantity = 0, period = "days" } = prepaidConfig;
    renewableDate.add(-periodQuantity, String(period).toLowerCase());
  }

  return renewableDate.toDate();
};

export const resolveAbleToRenewAffiliation = (affiliation) => {
  const result = { national: false, local: false };
  if (!affiliation) return result;

  const levels = ["national", "local"];
  for (const level of levels) {
    const levelAffiliation = affiliation[level];
    if (!levelAffiliation || !levelAffiliation._id) {
      result[level] = false;
      continue;
    }
    const details = levelAffiliation.details;
    if (!details || details.length === 0) {
      result[level] = true;
      continue;
    }
    result[level] = computeAbleToRenewByAffiliate(details[0]);
  }
  return result;
};

export const resolveNextRenewDate = (affiliation, hierarchyType = "ONE_LEVEL") => {
  if (!affiliation) return null;
  const national = affiliation.national && affiliation.national.details && affiliation.national.details[0];
  const local = affiliation.local && affiliation.local.details && affiliation.local.details[0];

  const candidates = [];
  if (hierarchyType === "ONE_LEVEL") {
    if (national) candidates.push(computeNextRenewDate(national));
  } else {
    if (national) candidates.push(computeNextRenewDate(national));
    if (local) candidates.push(computeNextRenewDate(local));
  }

  const valid = candidates.filter((d) => d && moment(d).isValid());
  if (valid.length === 0) return null;
  return valid.reduce((latest, current) =>
    moment(current).isAfter(latest) ? current : latest
  );
};

export const resolveCanAccessToCheckout = (user, ableToRenewAffiliation, canMakePayment) => {
  if (!user || user.role !== "MEMBER") return false;
  if (canMakePayment === false) return false;
  const isTemporallyAway = user.validationStatus === "TEMPORALLY_LEAVE";
  const hierarchyType =
    (user.headOffice && user.headOffice.hierarchyType) || "ONE_LEVEL";
  const national = !!(ableToRenewAffiliation && ableToRenewAffiliation.national);
  const local = !!(ableToRenewAffiliation && ableToRenewAffiliation.local);
  const renew = hierarchyType === "ONE_LEVEL" ? national : national || local;
  return isTemporallyAway || renew;
};

export const shouldHideRenewButton = (user) => {
  if (!user) return false;
  const status = user.validationStatus;
  const canApproveMembers =
    user.headOffice && user.headOffice.canApproveMembers === true;
  return (
    (status === "PROSPECTO" && canApproveMembers) || status === "TEMPORALLY_LEAVE"
  );
};
